/**
 * JARVIS greeting — replaces SYSTEMS ONLINE header.
 *
 * Renders a short greeting (where SYSTEMS ONLINE used to be). The
 * "outward thought" subtitle is intentionally not rendered here.
 *
 * Refresh strategy:
 *   - Fetch on mount
 *   - Refetch every 2 minutes while visible
 *   - Refetch when the window regains focus
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '@/services/api';

interface MindLatest {
  greeting: string;
  thought: string | null;
  thought_id: string | null;
  has_thought: boolean;
  subtitle: string | null;
  updated_at: string;
}

interface Props {
  fallbackGreeting?: string;
  fallbackThought?: string;
  refreshMs?: number;
  /** Where this is rendered — controls font size / layout. */
  variant?: 'hud' | 'floating';
}

export default function JarvisGreeting({
  fallbackGreeting = 'Ready when you are, sir.',
  fallbackThought,
  refreshMs = 5 * 60 * 1000, // 5 min
  variant = 'hud',
}: Props) {
  const [greeting, setGreeting] = useState<string>(fallbackGreeting);
  const [thought, setThought] = useState<string | null>(fallbackThought ?? null);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlight = useRef(false);

  const fetchGreeting = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await api.get<MindLatest>('/jarvis/mind/latest');
      if (res?.greeting) {
        setGreeting(res.greeting);
        setThought(res.thought || null);
        setLoaded(true);
      }
    } catch {
      // Silent — keep existing
    } finally {
      inFlight.current = false;
    }
  };

  useEffect(() => {
    fetchGreeting();
    timerRef.current = setInterval(fetchGreeting, refreshMs);
    const onFocus = () => fetchGreeting();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMs]);

  const headerClass =
    variant === 'hud'
      ? 'text-base font-display font-bold tracking-[0.2em] text-jarvis-blue glow-text mb-3 text-center transition-opacity duration-500 mx-auto max-w-xl px-4 leading-snug'
      : 'text-sm font-display font-bold tracking-[0.2em] text-jarvis-blue glow-text mb-3 text-center transition-opacity duration-500 mx-auto max-w-xl px-4 leading-snug';

  return (
    <>
      <h2
        className={headerClass}
        style={{
          opacity: loaded ? 1 : 0.8,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          wordBreak: 'normal',
        }}
      >
        {greeting.toUpperCase()}
      </h2>
    </>
  );
}
